import React, { useMemo } from 'react';
import { heroContent, heroPhaseContent } from '@/content';
import type { LaunchPhase } from '@/content/types';
import { createSmoothScrollHandler } from '@/utils/scrollToElement';
import { getViteMetaEnv } from '@/utils/viteMetaEnv';
import { PluginMockupContainer as PluginMockup } from '@/components/PluginMockup/PluginMockupContainer';
import { DynamicMeshGradientLazy as DynamicMeshGradient } from '@/components/DynamicMeshGradient/DynamicMeshGradientLazy';
import { FigmaCTA } from '@/components/FigmaCTA/FigmaCTA';
import { ContentRichText } from '@/components/ContentRichText';
import styles from './Hero.module.scss';

export const Hero: React.FC = () => {
  const handleSmoothScroll = createSmoothScrollHandler();
  
  // Get phase-specific CTA
  const launchPhase = (getViteMetaEnv().VITE_LAUNCH_MODE || 'stable') as LaunchPhase;
  const phaseCta = useMemo(() => {
    const phaseKeyMap: Record<LaunchPhase, keyof typeof heroPhaseContent> = {
      'pre-launch': 'preLaunch',
      'beta': 'beta',
      'early-bird': 'earlyBird',
      'stable': 'stable',
    };
    return heroPhaseContent[phaseKeyMap[launchPhase]]?.cta || heroContent.primaryCTA;
  }, [launchPhase]);
  
  // Check if CTA is an external Figma link
  const isFigmaLink = phaseCta.action.includes('figma.com');
  
  return (
    <section className={styles["hero"]} aria-labelledby="hero-title">
      {/* Animated mesh gradient background with responsive canvas resolution */}
      <DynamicMeshGradient
        className={styles["hero__background"]}
        distortion={0.45}
        swirl={0.3}
        speed={0.45}
        grainMixer={0.4}
        grainOverlay={0.15}
        rotation={76}
        resolutionScale="auto"
        maxWidth={1920}
        maxHeight={1080}
      />

      <div className={"section"}>
        <div className={`section__container section__container--inline-padding-sm ${styles["hero__content"]}`}>
          <div className={styles["hero__text"]}>
            <h1 id="hero-title" className={styles["hero__title"]} tabIndex={-1}>
              {heroContent.title.eyebrow ? (
                <span className={styles["hero__titleEyebrow"]}>{heroContent.title.eyebrow}</span>
              ) : null}
              {heroContent.title.text}
              <span className={styles["frame"]}>
                <span className={styles["frame__inner"]}>
                  <span className={`${styles["frame__handle"]} ${styles["frame__handle--tl"]}`}/>
                  <span className={`${styles["frame__handle"]} ${styles["frame__handle--tr"]}`}/>
                  <span className={`${styles["frame__handle"]} ${styles["frame__handle--bl"]}`}/>
                  <span className={`${styles["frame__handle"]} ${styles["frame__handle--br"]}`}/>
                </span>
                {heroContent.title.highlightedText}
              </span>
              {heroContent.title.suffix}
            </h1>
            <p className={styles["hero__subtitle"]}>
              <ContentRichText content={heroContent.subtitle} />
            </p>
            <div className={styles["hero__actions"]}>
              {isFigmaLink ? (
                <FigmaCTA
                  href={phaseCta.action}
                  location="hero"
                  className={styles["hero__button"]}
                >
                  {phaseCta.text}
                </FigmaCTA>
              ) : (
                <a
                  href={phaseCta.action}
                  onClick={handleSmoothScroll}
                  className={styles["hero__button"]}
                >
                  {phaseCta.text}
                </a>
              )}
            </div>
          </div>

          <div className={styles["hero__image"]}>
            <PluginMockup />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
